import type { EventObject, StateMachine, StateNodeConfig } from './types';
import { validateTargets } from './validate';

/**
 * Create a flat, typed state machine from a pure config. Both the context type
 * and the state-name union (`state.value`) are inferred from the arguments,
 * and every transition target (`initial`, `on`, `invoke.onDone`/`onError`) is
 * validated against the declared states — a typo'd target throws here, at
 * module load, instead of dead-ending the machine at runtime:
 *
 * ```ts
 * const context: Ctx = { region: null };
 * const machine = createStateMachine({
 *   id: 'provisionTeamUser',
 *   initial: 'collectProfile',
 *   context,
 *   states: { collectProfile: { on: { SUBMIT: 'setRegion' } }, ... },
 * });
 * ```
 */
export function createStateMachine<
  TContext,
  const TStates extends Record<string, StateNodeConfig<TContext, EventObject>>,
>(config: {
  id: string;
  initial: Extract<keyof TStates, string>;
  context: TContext;
  states: TStates;
}): StateMachine<TContext, Extract<keyof TStates, string>> {
  if (!config.states[config.initial]) {
    throw new Error(
      `[@heidi/flow] initial state "${String(config.initial)}" is not defined in states.`
    );
  }
  validateTargets(config.id, config.states);
  return {
    id: config.id,
    initial: config.initial,
    context: config.context,
    states: config.states as StateMachine<
      TContext,
      Extract<keyof TStates, string>
    >['states'],
    stateKeys: Object.keys(config.states),
  };
}
